import { useMemo } from 'react';
import { MdArrowBack } from 'react-icons/md';
import { Button } from '@/components/Button';
import Loading from '@/components/Loading';
import useFetchCandidateEndpoint from '../api/useFetchCandidateEndpoint';
import CandidatesProvidedJobPostCard from './CandidatesProvidedJobPostCard';
import type {
  AgentApiCandidateEndpointsCopySourceCandidate,
  AgentApiProvidedJobPostsRecord,
} from '@/types/serializers';

type Props = {
  sourceCandidate: AgentApiCandidateEndpointsCopySourceCandidate;
  onBack: () => void;
  selectedJobPosts: AgentApiProvidedJobPostsRecord[];
  setSelectedJobPosts: (jobPosts: AgentApiProvidedJobPostsRecord[]) => void;
};

export default function CandidatesJobPostEndpointsCopyJobPostsContents({
  sourceCandidate,
  onBack,
  selectedJobPosts,
  setSelectedJobPosts,
}: Props) {
  const { data, isLoading } = useFetchCandidateEndpoint({ id: sourceCandidate.id });

  const jobPosts = useMemo(
    () => data?.job_post_endpoints.map((endpoint) => endpoint.provided_job_post) ?? [],
    [data]
  );

  const isSelected = (jobPost: AgentApiProvidedJobPostsRecord) =>
    selectedJobPosts.some((jp) => jp.id === jobPost.id);

  const unselectedJobPosts = jobPosts.filter((jobPost) => !isSelected(jobPost));

  const handleSelectionChange = (jobPost: AgentApiProvidedJobPostsRecord, selected: boolean) => {
    if (selected) {
      setSelectedJobPosts([...selectedJobPosts, jobPost]);
    } else {
      setSelectedJobPosts(selectedJobPosts.filter((jp) => jp.id !== jobPost.id));
    }
  };

  const handleSelectAll = () => {
    setSelectedJobPosts([...selectedJobPosts, ...unselectedJobPosts]);
  };

  return (
    <div className="flex flex-col gap-md">
      <div className="flex items-center justify-between gap-md">
        <div className="flex min-w-0 items-center gap-md">
          <Button color="tertiary" size="md" type="button" onClick={onBack}>
            <MdArrowBack className="size-icon-xs" />
            求職者一覧に戻る
          </Button>
          <p className="truncate text-heading-xs text-main">{sourceCandidate.name}さんの提案求人</p>
        </div>
        {unselectedJobPosts.length > 0 && (
          <Button color="tertiary" size="md" type="button" onClick={handleSelectAll}>
            全ての求人を選択
          </Button>
        )}
      </div>
      {isLoading ? (
        <div className="flex min-h-[400px] items-center justify-center">
          <Loading size="small" />
        </div>
      ) : jobPosts.length === 0 ? (
        <div className="flex min-h-[400px] items-center justify-center">
          <p className="text-body-sm text-sub">コピーできる提案求人がありません</p>
        </div>
      ) : (
        <div className="flex flex-col gap-lg">
          {jobPosts.map((jobPost) => (
            <CandidatesProvidedJobPostCard
              key={jobPost.id}
              providedJobPost={jobPost}
              isSelected={isSelected(jobPost)}
              onSelectionChange={(selected) => handleSelectionChange(jobPost, selected)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
